import React, { createContext, useCallback, useContext, useMemo, useState } from 'react';
import { Toast, ToastContainer, Button } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { BILLING_PATH, isUpgradeError } from '../utils/upgradeError';

type ToastType = 'success' | 'error' | 'info';

export type ToastShowOptions = {
    actionLabel?: string;
    onAction?: () => void;
    /** Autohide duration in ms. Defaults: action toasts 15000, upgrade errors 7000, others 3500. */
    durationMs?: number;
    /** Show countdown bar under the toast. Defaults to true when an action is present. */
    showTimer?: boolean;
};

interface ToastItem {
    id: number;
    type: ToastType;
    message: string;
    actionLabel?: string;
    onAction?: () => void;
    durationMs: number;
    showTimer: boolean;
}

interface ToastContextType {
    showSuccess: (message: string, options?: ToastShowOptions) => void;
    showError: (message: string, options?: ToastShowOptions) => void;
    showInfo: (message: string, options?: ToastShowOptions) => void;
}

const ToastContext = createContext<ToastContextType | null>(null);

const DEFAULT_DURATION_MS = 3500;
const ACTION_DURATION_MS = 15000;
const UPGRADE_DURATION_MS = 7000;

const getToastBackground = (type: ToastType) => {
    switch (type) {
        case 'success':
            return 'success';
        case 'error':
            return 'danger';
        default:
            return 'primary';
    }
};

export const ToastContextProvider = ({ children }: { children: React.ReactNode }) => {
    const [toasts, setToasts] = useState<ToastItem[]>([]);
    const navigate = useNavigate();

    const removeToast = useCallback((id: number) => {
        setToasts((prev) => prev.filter((toast) => toast.id !== id));
    }, []);

    const enqueue = useCallback((type: ToastType, message: string, options?: ToastShowOptions) => {
        const hasAction = Boolean(options?.actionLabel && options?.onAction);
        const id = Date.now() + Math.floor(Math.random() * 1000);
        const durationMs =
            options?.durationMs ??
            (hasAction
                ? ACTION_DURATION_MS
                : type === 'error' && isUpgradeError(message)
                  ? UPGRADE_DURATION_MS
                  : DEFAULT_DURATION_MS);

        setToasts((prev) => [
            ...prev,
            {
                id,
                type,
                message,
                actionLabel: options?.actionLabel,
                onAction: options?.onAction,
                durationMs,
                showTimer: options?.showTimer ?? hasAction,
            },
        ]);
    }, []);

    const value = useMemo(
        () => ({
            showSuccess: (message: string, options?: ToastShowOptions) =>
                enqueue('success', message, options),
            showError: (message: string, options?: ToastShowOptions) =>
                enqueue('error', message, options),
            showInfo: (message: string, options?: ToastShowOptions) =>
                enqueue('info', message, options),
        }),
        [enqueue],
    );

    return (
        <ToastContext.Provider value={value}>
            {children}
            <ToastContainer position="top-end" className="p-3" style={{ zIndex: 2000 }}>
                {toasts.map((toast) => {
                    const upgrade = toast.type === 'error' && isUpgradeError(toast.message);
                    const hasAction = Boolean(toast.actionLabel && toast.onAction);
                    return (
                        <Toast
                            key={toast.id}
                            className={hasAction ? 'app-toast app-toast--accent' : 'app-toast'}
                            autohide
                            delay={toast.durationMs}
                            bg={hasAction ? undefined : getToastBackground(toast.type)}
                            onClose={() => removeToast(toast.id)}
                        >
                            <Toast.Body className={`${hasAction ? 'app-toast__body' : 'text-white app-toast__body'}`}>
                                <div className="app-toast__content">
                                    <span className="app-toast__message">{toast.message}</span>
                                    {hasAction && (
                                        <Button
                                            variant="light"
                                            size="sm"
                                            className="app-toast__action"
                                            onClick={() => {
                                                toast.onAction?.();
                                                removeToast(toast.id);
                                            }}
                                        >
                                            {toast.actionLabel}
                                        </Button>
                                    )}
                                    {upgrade && (
                                        <Button
                                            variant="link"
                                            className="p-0 align-baseline text-white text-decoration-underline"
                                            onClick={() => {
                                                removeToast(toast.id);
                                                navigate(BILLING_PATH);
                                            }}
                                        >
                                            Go to Billing
                                        </Button>
                                    )}
                                </div>
                                {toast.showTimer && (
                                    <span
                                        className="app-toast__timer"
                                        style={{ animationDuration: `${toast.durationMs}ms` }}
                                        aria-hidden
                                    />
                                )}
                            </Toast.Body>
                        </Toast>
                    );
                })}
            </ToastContainer>
        </ToastContext.Provider>
    );
};

export const useToast = () => {
    const ctx = useContext(ToastContext);
    if (!ctx) {
        throw new Error('useToast must be used within ToastContextProvider');
    }

    return ctx;
};
